/**
 * EntitlementProvenanceList
 *
 * Read-only display of a user's active entitlements with accurate per-source
 * provenance. Replaces the previous two-bucket (manual vs subscription) view,
 * which forced a multi-valued `sources[]` set into mutually-exclusive buckets
 * and so hid provenance (e.g. Supapass-only grants were invisible, and
 * dual-granted entitlements hid their subscription source).
 *
 * Each row shows the FULL set of sources as compact badges (the fix is visible
 * without expanding) and discloses per-source detail on demand. Display only —
 * editing manual grants remains in EntitlementEditor.
 *
 * The badges are grouped and state-split by `resolveProvenanceChips`, which
 * exists because this component used to render one badge per grant with no test
 * on `isActive` — dressing a dead Stripe subscription as a live source of a
 * live entitlement. Nothing was removed to fix that; a dead source still shows,
 * greyed and labelled `. ended`.
 *
 * ONLY ACTIVE ENTITLEMENTS APPEAR HERE, and that is invisible from the list
 * itself: `getActiveEntitlements` drops an entitlement inactive from every
 * source before it reaches the wire. The panel's own subtitle says so, because
 * an admin cannot otherwise tell "never had this" from "had it and lost it".
 */

import { useId, useState } from 'react';
import type {
  EntitlementDetail,
  SubscriptionEntitlementData,
  UserEntitlements,
} from '../../lib/types/entitlement';
import { formatUtc } from '../../lib/utils/subscriptionFormat';
import {
  chipText,
  chipTitle,
  provenanceLabel,
  resolveProvenanceChips,
  type ProvenanceChip,
} from '../../lib/utils/entitlementProvenance';

interface EntitlementProvenanceListProps {
  entitlements: UserEntitlements | undefined;
}

/**
 * Badge colour per provenance kind. Manual = sky; stripe = purple; any other
 * provider = emerald. Provider-driven so a Supapass/Patreon grant is visibly
 * distinct from a Stripe one.
 *
 * A grant that is no longer granting takes neutral grey regardless of provider:
 * the colours above say "this is what is paying for the user", and a dead grant
 * must not borrow that.
 */
function provenanceBadgeClass(chip: ProvenanceChip): string {
  if (!chip.active) {
    return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
  }
  switch (chip.kind) {
    case 'manual':
      return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200';
    case 'stripe':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    default:
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
  }
}

/** Bare expiry value for a labelled field, e.g. an "Expires" definition row. */
function expiryValue(expiresAt: string | null): string {
  return expiresAt ? formatUtc(expiresAt) : 'Non-expiring';
}

/** "Expires" or "Expired", by whether the date has passed. */
function expiryLabel(expiresAt: string | null): string {
  if (!expiresAt) return 'Expires';
  return new Date(expiresAt).getTime() < Date.now() ? 'Expired' : 'Expires';
}

/**
 * Expiry phrase for the collapsed row. Past dates read "Expired", not
 * "Expires" — a future-tense label over a date months in the past was one of
 * the things that made this card hard to trust.
 */
function expiryText(expiresAt: string | null): string {
  if (!expiresAt) return 'Non-expiring';
  const past = new Date(expiresAt).getTime() < Date.now();
  return `${past ? 'Expired' : 'Expires'} ${formatUtc(expiresAt)}`;
}

/** A small definition-list row used inside the expanded detail panel. */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
      <dd className="text-gray-700 dark:text-gray-200 break-all">{value}</dd>
    </div>
  );
}

function ProvenanceBadge({ chip }: { chip: ProvenanceChip }) {
  return (
    <span
      title={chipTitle(chip)}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${provenanceBadgeClass(
        chip
      )}`}
    >
      {chipText(chip)}
    </span>
  );
}

/**
 * Detail for one subscription record, provider-labelled. Renders only the fields
 * present (records carry optional fields per provider).
 */
function SubscriptionDetail({ sub }: { sub: SubscriptionEntitlementData }) {
  return (
    <div>
      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
        {provenanceLabel(sub.provider)}
        {!sub.isActive && (
          <span className="font-normal text-gray-500 dark:text-gray-400">
            {' '}
            · ended
          </span>
        )}
      </h5>
      <dl className="space-y-0.5">
        {sub.productIdentifier && (
          <DetailRow label="Product" value={sub.productIdentifier} />
        )}
        {sub.providerSubscriptionId && (
          <DetailRow
            label="Subscription ID"
            value={sub.providerSubscriptionId}
          />
        )}
        {sub.willRenew !== undefined && (
          <DetailRow label="Auto-renews" value={sub.willRenew ? 'Yes' : 'No'} />
        )}
        {sub.purchaseDate && (
          <DetailRow label="Started" value={formatUtc(sub.purchaseDate)} />
        )}
        <DetailRow label="Active" value={sub.isActive ? 'Yes' : 'No'} />
        <DetailRow
          label={expiryLabel(sub.expiresAt)}
          value={expiryValue(sub.expiresAt)}
        />
      </dl>
    </div>
  );
}

/** Detail for the manual grant. */
function ManualDetail({
  detail,
  active,
}: {
  detail: EntitlementDetail;
  active: boolean;
}) {
  const man = detail.manual;
  return (
    <div>
      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
        Manual
        {!active && (
          <span className="font-normal text-gray-500 dark:text-gray-400">
            {' '}
            · ended
          </span>
        )}
      </h5>
      {man ? (
        <dl className="space-y-0.5">
          <DetailRow label="Granted by" value={man.grantedBy} />
          <DetailRow label="Reason" value={man.reason} />
          <DetailRow label="Granted" value={formatUtc(man.grantedAt)} />
          <DetailRow
            label={expiryLabel(man.expiresAt)}
            value={expiryValue(man.expiresAt)}
          />
        </dl>
      ) : (
        <p className="text-gray-400 dark:text-gray-500">No detail available</p>
      )}
    </div>
  );
}

function ProvenanceRow({
  id,
  detail,
}: {
  id: string;
  detail: EntitlementDetail;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const label = detail.name ?? id;
  // Defensive: tolerate a missing/empty subscriptions array without crashing.
  const subscriptions = detail.subscriptions ?? [];
  const hasManual = detail.manual !== undefined;
  // Presence of the block is NOT liveness. The evaluator emits `manual`
  // whenever a manual record exists so admin-conferred access is never hidden,
  // but only lists 'manual' in `sources` when that grant is actually granting —
  // an expired hand-grant has the block and not the source.
  const manualActive = (detail.sources ?? []).includes('manual');

  return (
    <li
      data-testid={`entitlement-row-${id}`}
      className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1.5 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-sky-700 dark:hover:text-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
          >
            <svg
              className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="truncate">{label}</span>
          </button>
          <p className="ml-5 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {/*
              Hardcoded green, and correct only because of the
              `detail.isActive === true` filter in the list below — the backend
              drops inactive entitlements before that anyway. If anything ever
              surfaces a lapsed entitlement in this list, this dot goes with it
              and will call it Active.
            */}
            <span className="inline-flex items-center">
              <span
                className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"
                aria-hidden="true"
              />
              Active
            </span>
            <span className="mx-1.5">·</span>
            {expiryText(detail.expiresAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          {resolveProvenanceChips(subscriptions, {
            present: hasManual,
            active: manualActive,
          }).map(chip => (
            <ProvenanceBadge
              key={`${chip.kind}-${chip.active ? 'active' : 'ended'}`}
              chip={chip}
            />
          ))}
        </div>
      </div>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={`${label} source detail`}
          className="ml-5 mt-2 text-xs"
        >
          {/*
            The description restates the row's own title on every row —
            "Access to HeapsterLive" under "HeapsterLive Access" — so it costs a
            line on each of five rows and tells a reader nothing they have not
            just read. It stays available here, where somebody who wants the
            detail has already asked for it.
          */}
          {detail.description && (
            <p className="mb-2 text-gray-500 dark:text-gray-400">
              {detail.description}
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {subscriptions.map((sub, i) => (
              <SubscriptionDetail
                key={`sub-${sub.provider}-${sub.providerSubscriptionId ?? i}`}
                sub={sub}
              />
            ))}
            {hasManual && (
              <ManualDetail detail={detail} active={manualActive} />
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export function EntitlementProvenanceList({
  entitlements,
}: EntitlementProvenanceListProps) {
  const activeEntries = Object.entries(entitlements ?? {})
    .filter(([, detail]) => detail.isActive === true)
    .sort(([aId, a], [bId, b]) => (a.name ?? aId).localeCompare(b.name ?? bId));

  if (activeEntries.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No active entitlements
      </p>
    );
  }

  return (
    <ul className="divide-gray-100 dark:divide-gray-700">
      {activeEntries.map(([id, detail]) => (
        <ProvenanceRow key={id} id={id} detail={detail} />
      ))}
    </ul>
  );
}
